import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type { UserSummary } from "./types";
import { USERS_LIST_QUERY_KEY, USER_DETAIL_QUERY_KEY } from "./constants";

/**
 * ADR-0009 (Amendment 1) — `POST /api/admin/users/:id/test-account`
 * (WANDERFIT_ADMIN only, never self-service). Sets or clears the prod
 * test-partition flag; the API re-stamps every property and venue the account
 * owns in the same transaction.
 *
 * Hand-written against `httpClient` rather than the Orval-generated function:
 * the generated call sites are unusable at runtime (envelope-vs-body
 * mismatch). See CLAUDE.md → TD-07.
 *
 * Throws `ApiError` on 404 (USER_NOT_FOUND) and on the 409 that refuses a
 * test → real move while the entities carry demo activity
 * (ACCOUNT_HAS_ACTIVITY; the pre-amendment API sent ACCOUNT_HAS_ENTITIES) —
 * both are decoded by `resolveTestAccountError`.
 */
export interface SetTestAccountVariables {
  readonly id: string;
  readonly isTestAccount: boolean;
}

async function setTestAccount({
  id,
  isTestAccount,
}: SetTestAccountVariables): Promise<UserSummary> {
  return httpClient<UserSummary>(`/api/admin/users/${encodeURIComponent(id)}/test-account`, {
    method: "POST",
    // `httpClient` deliberately does NOT default the content-type (see its
    // header comment). Without it the API never parses the JSON body and the
    // DTO validator rejects the request with "isTestAccount must be a boolean
    // value" — the toggle failed in prod until this header was added.
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ isTestAccount }),
  });
}

export function useSetTestAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setTestAccount,
    onSuccess: (_data, { id }) => {
      // Both surfaces render the TEST badge, so both must be refetched —
      // a stale row in the list would contradict the detail header.
      queryClient.invalidateQueries({ queryKey: USERS_LIST_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...USER_DETAIL_QUERY_KEY, id] });
    },
  });
}
